import { supabase } from '@/integrations/supabase/client';

// ─── Types ───────────────────────────────────────────

export type ProfileData = { display_name: string | null; avatar_url: string | null };
export type ProfileMap = Record<string, ProfileData>;
export type AdminProfileResult = {
  profileMap: Map<string, { display_name: string | null; avatar_url: string | null }>;
  emailMap: Map<string, string>;
};

// ─── Private table (profiles) — for community/ensino ───

export async function buildProfileMap(userIds: string[]): Promise<ProfileMap> {
  if (userIds.length === 0) return {};
  const { data, error } = await (supabase as any).rpc('list_forjahub_public_profiles', {
    p_user_ids: [...new Set(userIds)].slice(0, 100),
  });
  if (error) throw error;
  const map: ProfileMap = {};
  for (const p of (data ?? []) as any[]) map[p.user_id] = { display_name: p.display_name, avatar_url: p.avatar_url };
  return map;
}

export async function enrichWithProfiles<T extends { user_id: string }>(
  rows: T[]
): Promise<(T & { profiles: ProfileData | null })[]> {
  if (rows.length === 0) return [];
  const map = await buildProfileMap([...new Set(rows.map(r => r.user_id))]);
  return rows.map(r => ({ ...r, profiles: map[r.user_id] ?? null }));
}

export async function fetchSingleProfile(userId: string): Promise<ProfileData | null> {
  const map = await buildProfileMap([userId]);
  return map[userId] ?? null;
}

// ─── Public view (public_profiles) — for marketplace/admin ───

export async function buildPublicProfileMap(userIds: string[]): Promise<ProfileMap> {
  return buildProfileMap(userIds);
}

// ─── Admin enrichment (public_profiles + admin_users_view) ───

export async function buildAdminProfileMap(userIds: string[]): Promise<AdminProfileResult> {
  if (userIds.length === 0) {
    return { profileMap: new Map(), emailMap: new Map() };
  }

  const map = await buildProfileMap(userIds);

  const profileMap = new Map(
    Object.entries(map).map(([userId, profile]) => [userId, profile])
  );
  // Auth e-mail is intentionally not exposed by the public profile bridge.
  const emailMap = new Map<string, string>();

  return { profileMap, emailMap };
}
